/**
 * 파일명 : com0101p.tsx
 * 설명 : 결재선 조회
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 */

/************************************************
 * Import 영역  
 ************************************************/
/************************************************
 * Import 영역
 ************************************************/
/* React */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0101p1Action } from '../../../redux/slice/com/com0101p1_slice';
import { IInquiryCndsState, ITargetItemState, IUserItemState, IDeptTreeState } from '../../../redux/models/com/com0101p1_model';

/* AntD */
import { Layout, Row, Col, Space, Button, Tree, Table, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { GetComponentProps } from 'rc-table/lib/interface';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

/************************************************
 * 전역변수 선언영억
 ************************************************/
const Com0101p1 = (props:any) => {
	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const deptTree   = useAppSelector((state) => state.Com0101p1.deptTree);
    const treeKey    = useAppSelector((state) => state.Com0101p1.treeKey);
    const targetList = useAppSelector((state) => state.Com0101p1.targetList);
    const userList   = useAppSelector((state) => state.Com0101p1.userList);

    let myDeptNode:IDeptTreeState;
    const nodeRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);


    /************************************************
     * 화면 초기화 이벤트
     ************************************************/
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        dispatch(Com0101p1Action.setTargetList([]));
        const headerHeight = 520;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);

        if (!deptTree || deptTree.length < 1) {
            // 입력파라미터
            const requestData = {};
    
            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd : 'TRCOMR0003',
                success : (responseData:any) => {
                    dispatch(Com0101p1Action.setDeptTree(responseData.deptTree));
                    dispatch(Com0101p1Action.setMaxLvlCd(responseData.lastLvl));
    
    
                    const target:ITargetItemState = {
                        dnm    : '상신자',
                        apprEmpNo  : common.getEmpNo(),
                        apprUserNm : common.getUserNm(),
                    };
                    dispatch(Com0101p1Action.setTargetList([target]));
                },
            });
        } else {
            const target:ITargetItemState = {
                dnm    : '상신자',
                apprEmpNo  : common.getEmpNo(),
                apprUserNm : common.getUserNm(),
            };
            dispatch(Com0101p1Action.setTargetList([target]));
        }

    }, []);

    const findNodeMyDept = (treeData:IDeptTreeState[]) => {
        for (const node of treeData) {
            if (node.key === common.getDeptCd()) return node;
      
            if (node.children) {
                const found:IDeptTreeState = findNodeMyDept(node.children);

                if (found) return found;
            }
        }
        return null;
    };

    React.useEffect(() => {
        if (deptTree && deptTree.length > 0 && (!myDeptNode || Object.keys(myDeptNode).length < 1)) {
            myDeptNode = findNodeMyDept(deptTree);

            if (myDeptNode) {
                setDeptUsers(myDeptNode.key);
            }
        }
    }, [deptTree]);


    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btnClose': {
                props.cancel();
                break;
            }
            case 'btnOk': {
                props.accept(targetList.filter((item:ITargetItemState) => {
                    return item.apprEmpNo !== common.getEmpNo();
                }));
                break;
            }
            default:
        }
    };

    /**
     * 기능 : Tree 선택 이벤트 선언
     * param : 
     */
    const onSelect = (_keys:any, event:any) => {
        setDeptUsers(event.node.key);
    };

    const setDeptUsers = (deptCd:string) => {
        const requestData:IInquiryCndsState = {
            deptCd : deptCd,
            userStCd : 'A',
        };
        
        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0003',
            success : (responseData:any) => {
                dispatch(Com0101p1Action.setUserList(responseData.userList));
                dispatch(Com0101p1Action.setTreeKey(deptCd));

                if (deptCd && nodeRefs.current[deptCd]) {
                    nodeRefs.current[deptCd].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            },
        });
    }

    /**
     * 기능 : onRow
     */
    const onUserRow: GetComponentProps<IUserItemState> = (record:IUserItemState, _rowIndex) => ({
        onClick: (_event:any) => {
            const itemTarget:ITargetItemState = { 
                           dnm        : '결재자'
                         , ord        : record.key + 1
                         , apprEmpNo  : record.empNo
                         , apprUserNm : record.userNm 
                         , apprOrd    : targetList.length
                         , postNm     : record.postNm
                         , dutyNm     : record.dutyNm
                         , deptNm     : record.deptNm
                         , apprStNm   : '대기'
            };
            dispatch(Com0101p1Action.addTargetList(itemTarget));
        },
    });
    const onTargetRow: GetComponentProps<ITargetItemState> = (record:ITargetItemState, _rowIndex) => ({
        onClick: (_event:any) => {
            if (record.apprEmpNo === common.getEmpNo()) {
                return;
            }
            const arrFilterd:ITargetItemState[] = targetList.filter((item:ITargetItemState) => {
                return item.apprEmpNo !== record.apprEmpNo;
            });

            dispatch(Com0101p1Action.setTargetList(arrFilterd));
        },
    });

    /************************************************
     * 개발자함수 영역
     ************************************************/


    /************************************************
     * Render 변수 선언 및 셋팅영역
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */


     /* Render 변수 선언영역 */
     /* AntD */
     const { Content } = Layout;
     const { Text }    = Typography;

    /* Table Columns */
    const columnsUsers = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '50%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '30%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>이름</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: '20%',
            }];
            return arr;
    }, []);

    const columnsTarget = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>구분</div>,
                dataIndex: 'dnm',
                key: 'dnm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>이름</div>,
                dataIndex: 'apprUserNm',
                key: 'apprUserNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>삭제</div>,
                key: 'action',
                render: (_text:string, record:ITargetItemState) => {
                    if (record.apprEmpNo !== common.getEmpNo()) {
                        return (
                            <Button
                                id='btn_showDetail'
                                name='btn_showDetail'
                                className='link-button'
                                size='small'
                                icon={<DeleteOutlined style={{color:'red'}}/>}
                                onClick={() => {
                                    let arrTarget = [...targetList];
                                    arrTarget = arrTarget.filter((item:ITargetItemState) => {
                                        return item.apprEmpNo !== record.apprEmpNo;
                                    });
                                    dispatch(Com0101p1Action.setTargetList(arrTarget));
                                }}
                            />
                        )
                    } else {
                        return null;
                    }
                },
                align: 'center',
                width: '5%',
            }];
            return arr;
    }, []);


    return (
        /* 화면 디자인 영역*/
        <Layout>
            <Content>
                <Row justify='end'>
                    <Col>
                        <Space direction='vertical'>
                            <Row justify='space-around'>
                                <Space size='small'>
                                    <Button
                                        size='small'
                                        name='btnClose'
                                        className='background-color-default-button'
                                        onClick={onClick}
                                    >
                                        닫기
                                    </Button>
                                    <Button
                                        size='small'
                                        name='btnOk'
                                        className='background-color-primary-button'
                                        onClick={onClick}
                                    >
                                        확인
                                    </Button>
                                </Space>
                            </Row>
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {deptTree && deptTree.length > 0 ?
                        <Tree
                            showLine
                            prefixCls='dept-tree'
                            className='dept-tree'
                            defaultExpandAll={true}
                            onSelect={onSelect}
                            treeData={deptTree}
                            selectedKeys={[treeKey]}
                            style={{maxHeight : '150px', overflowY : 'auto'}}
                            titleRender={(nodeData) => (
                                <span ref={(el) => {
                                    if (el) {
                                        nodeRefs.current[nodeData.key] = el;
                                    }
                                }}>
                                    {nodeData.title}
                                </span>
                              )}
                        /> : null}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Row>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>사용자목록</Text>
                            </Space>
                            <Col>
                                <Table
                                    size='small'
                                    dataSource={userList}
                                    key='empNo'
                                    columns={columnsUsers()}
                                    style={{width:'100%'}}
                                    scroll={{y:150}}
                                    pagination={false}
                                    onRow={onUserRow}
                                />
                            </Col>
                        </Row>
                        <br/>
                            <Row>
                                <Space>
                                    <img className='title-icon' alt='' />
                                    <Text strong>결재자목록</Text>
                                </Space>
                                <div style={{ flex: '1 1 auto', height : contentHeight, overflowY: 'auto'}}>
                                    <Col>
                                        <Table
                                            size='small'
                                            dataSource={targetList}
                                            key='apprEmpNo'
                                            columns={columnsTarget()}
                                            style={{width:'100%'}}
                                            scroll={{y:150}}
                                            pagination={false}
                                            onRow={onTargetRow}
                                        />
                                    </Col>
                                </div>
                            </Row>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
}

export default Com0101p1;